import { useEffect, useState } from 'react';
import { apiService } from '@/lib/api';

/**
 * Stripe Checkout returns to `?billing=success` (or `billing=cancelled`), but activation happens
 * asynchronously via the webhook — so the org may still read as not-Active for a few seconds. On
 * `success` this polls billing until the status flips to Active, then reloads so the app re-evaluates
 * lock state; if it doesn't flip within ~30s it reports `timeout` ("payment is processing"). The
 * query flag is stripped from the URL so a refresh doesn't re-trigger it.
 */
export type CheckoutReturn = 'idle' | 'processing' | 'cancelled' | 'timeout' | 'success';

export function useCheckoutReturn(): CheckoutReturn {
    const [state, setState] = useState<CheckoutReturn>('idle');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const flag = params.get('billing');
        if (flag !== 'success' && flag !== 'cancelled' && flag !== 'done') return;

        // Strip the flag so a manual refresh doesn't replay this.
        params.delete('billing');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));

        if (flag === 'cancelled') {
            setState('cancelled');
            return;
        }

        // Set by the reload below, once activation actually landed — the confirmation the user sees.
        if (flag === 'done') {
            setState('success');
            return;
        }

        setState('processing');
        let stopped = false;
        let tries = 0;
        const tick = async () => {
            tries++;
            try {
                const d = await apiService.getBilling();
                // Reload once access is actually restored, i.e. the paid-through period is in the
                // future — not merely when status is Active. This is the right signal for BOTH cases:
                //  • First subscribe: activation sets a 3-day guard (ends_at = now+3d) up front, so this
                //    is true as soon as status flips Active and the user gets into the app (even if the
                //    charge is still settling — the guard covers it, and a decline shows the Settings
                //    banner).
                //  • Payment lapse (already stored Active, ends_at in the PAST → derived Locked): stays
                //    false until the charge lands and ApplyPaidThrough pushes ends_at forward, so it
                //    doesn't bounce the user straight back onto the lock screen.
                const endsMs = d.current_period_ends_at ? new Date(d.current_period_ends_at).getTime() : 0;
                if (d.status === 'Active' && endsMs > Date.now()) {
                    // Reload (so the app re-evaluates lock state) carrying billing=done, otherwise the
                    // reload wipes every trace of the payment and the user lands on a silent page.
                    params.set('billing', 'done');
                    window.location.replace(`${window.location.pathname}?${params.toString()}`);
                    return;
                }
            } catch {
                // ignore and keep polling
            }
            if (stopped) return;
            if (tries >= 10) {
                setState('timeout');
                return;
            }
            setTimeout(tick, 3000);
        };
        const first = setTimeout(tick, 1500);
        return () => {
            stopped = true;
            clearTimeout(first);
        };
    }, []);

    return state;
}
